import React, { useState, useEffect } from "react";
import { graphql, Link } from "gatsby";
import pathOr from "ramda/src/pathOr";
import pipe from "ramda/src/pipe";
import sort from "ramda/src/sort";
import cond from "ramda/src/cond";
import equals from "ramda/src/equals";
import groupBy from "ramda/src/groupBy";
import { css } from "styled-components";
import PropTypes from "prop-types";
import { Box, Flex } from "rebass";
import d from "dayjs";
import { Paragraph } from "../components/Text";
import SEO from "../components/seo";
import { ColorOverlay } from "../components/ui/ColorOverlay";
import Timeline from "../components/Timeline";
import SchemaCard from "../components/SchemaCard";
import { isBetweenMidnightAndBreakTime } from "../utils/schema";

interface Event {
  id: string;
  start: string;
  end: string;
  startTime: string;
  endTime: string;
  title: string;
  cancelled: null;
  timeHasChanged: null;
  eventVenue: EventVenue;
  act: Act;
}

interface Act {
  id: string;
  slug: string;
  image: Image;
  name: string;
}

interface Image {
  fluid: Fluid;
}

interface Fluid {
  base64: string;
  aspectRatio: number;
  src: string;
  srcSet: string;
  sizes: string;
}

interface EventVenue {
  title: string;
}

type DateIshValue = string | Date | d.Dayjs;

const isLessThan45Min = (event: Event) =>
  d(event.end).diff(event.start, "minutes") <= 45;

const BREAK_HOUR = 7;
const FESTIVAL_DATES = { start: "2021-08-19", end: "2021-08-22" };

const getEvents = pathOr<Event[]>(
  [],
  ["events", "edges", 0, "node", "artistEvents"]
);
const hideSchema = pathOr(false, ["flags", "edges", 0, "node", "hideSchema"]);

const splitEveryByEven = (list: Event[]) =>
  (list || []).reduce(
    (acc, val, index) => {
      acc[index % 2 === 0 ? 0 : 1].push(val);
      return acc;
    },
    [[], []]
  );

const groupByDay = groupBy((event: Event) => {
  const format = (dt: d.Dayjs) => dt.format("YYYY-MM-DD");
  const startDate = d(event.start);
  const endDate = d(event.end);
  const breakTime = d(endDate)
    .set("hour", BREAK_HOUR)
    .set("minute", 0);

  if (startDate.isSame(endDate, "day")) {
    if (isBetweenMidnightAndBreakTime(startDate, breakTime)) {
      return format(startDate.date(startDate.date() - 1));
    }
    return format(startDate);
  }
  return endDate.isBefore(breakTime) ? format(startDate) : format(endDate);
});

const sortByTime = sort((eventA: Event, eventB: Event) => {
  const startA = d(eventA.start);
  const startB = d(eventB.start);
  if (startA.isSame(startB)) return 0;
  if (startA.isAfter(startB)) return 1;
  return -1;
});

const eventsByDay = pipe(getEvents, sortByTime, groupByDay);

const dayLabel = cond([
  [equals(0), () => "Sön"],
  [equals(6), () => "Lör"],
  [equals(5), () => "Fre"],
  [equals(4), () => "Tor"],
]);

const createLinkToArtist = (artist) => (artist ? `/artist/${artist.slug}` : "");

const EventCard = ({
  topOffset,
  end,
  start,
  title,
  image,
  venue,
  act = {},
  ...rest
}) => {
  const Tag = !image ? Box : Link;
  return (
    <Tag style={{ cursor: "pointer" }} to={createLinkToArtist(act)}>
      <SchemaCard
        topOffset={topOffset}
        title={title}
        image={image}
        venue={venue}
        startDate={start}
        endDate={end}
        {...rest}
      />
    </Tag>
  );
};

EventCard.propTypes = {
  topOffset: PropTypes.number,
  end: PropTypes.string,
  start: PropTypes.string,
  title: PropTypes.string,
  image: PropTypes.object,
  venue: PropTypes.string,
  act: PropTypes.object,
};

const EventDays = ({ day, active, date, handleClick }) => (
  <Box
    onClick={() => handleClick(date)}
    m={1}
    my={2}
    p={1}
    css={{ cursor: "pointer", borderBottom: active ? "3px solid #ffe200" : "" }}
  >
    <Paragraph
      fontFamily="regular"
      color="red"
      m={0}
      fontSize={[2, 3, 3]}
      fontWeight="normal"
    >
      {day}
    </Paragraph>
  </Box>
);

EventDays.propTypes = {
  day: PropTypes.string,
  active: PropTypes.bool,
  date: PropTypes.string,
  handleClick: PropTypes.func,
};

const INTERVAL_MINUTES = 60;
const INTERVAL_PX = 280;

const calculateTopOffset = (
  startTime: DateIshValue,
  indexTime: DateIshValue
) => {
  let diffInMinutes = d(startTime).diff(d(indexTime), "minutes");
  if (diffInMinutes < 0) {
    diffInMinutes += 24 * 60;
  }
  return (diffInMinutes || 0) * (INTERVAL_PX / INTERVAL_MINUTES);
};

const calculateEventCardHeight = (
  startTime: DateIshValue,
  endTime: DateIshValue
) => {
  const diffInMinutes = d(endTime).diff(d(startTime), "minutes");
  return (diffInMinutes || 0) * (INTERVAL_PX / INTERVAL_MINUTES);
};

const CardRow = ({ children, ...props }) => (
  <Flex
    width={1}
    alignItems="flex-start"
    mr={0}
    justifyContent="center"
    flexDirection="column"
    {...props}
  >
    {children}
  </Flex>
);
CardRow.propTypes = {
  children: PropTypes.node,
};

function getCurrentFestivalDate() {
  const start = d(FESTIVAL_DATES.start);
  const end = d(FESTIVAL_DATES.end);
  const now = d();
  const breakTime = now
    .add(1, "day")
    .set("hour", BREAK_HOUR)
    .set("minutes", 0);

  if (now.isBefore(start, "day") || now.isAfter(end, "day")) {
    return start.format("YYYY-MM-DD");
  }
  if (
    (now.isAfter(start, "day") && now.isBefore(end, "day")) ||
    now.isSame(start, "day") ||
    now.isSame(end, "day")
  ) {
    if (
      isBetweenMidnightAndBreakTime(now, breakTime) &&
      !now.isSame(start, "day")
    ) {
      return now.subtract(1, "day").format("YYYY-MM-DD");
    }
    return now.format("YYYY-MM-DD");
  }
  return start.format("YYYY-MM-DD");
}

const SchemaPage = ({ data }) => {
  const hide = hideSchema(data);
  if (hide) {
    if (
      typeof window !== "undefined" &&
      !window.location.search.includes("fomo")
    ) {
      window.location.href = "/";
    }
  }

  const [days] = useState(eventsByDay(data));
  const [activeDay, setActiveDay] = useState(Object.keys(days)[0]);

  const startTime = days[activeDay]?.[0].start;
  const endTime = days[activeDay]?.[days[activeDay].length - 1].end;

  const [e1, e2] = splitEveryByEven(days[activeDay]);

  useEffect(() => {
    const date = getCurrentFestivalDate();
    if (!days[date]) {
      setActiveDay(FESTIVAL_DATES.start);
    } else {
      setActiveDay(getCurrentFestivalDate());
    }
  }, []);

  return (
    <div>
      <SEO
        title="PLX Tjärö 2021 - schema"
        keywords={["festival", "tjärö", "schema"]}
      />
      <Box
        css={css`
          z-index: 1;
          position: relative;
          margin-bottom: 100px;
        `}
      >
        <Flex
          flexDirection="row"
          justifyContent="center"
          mx={[-3, -3, -4]}
          bg="black"
          color="red"
          css={css`
            border-bottom: 1px solid;
            position: sticky;
            top: 0;
            z-index: 1;
          `}
        >
          {Object.keys(days).map((date) => (
            <EventDays
              key={date}
              active={date === activeDay}
              handleClick={setActiveDay}
              date={date}
              day={dayLabel(d(date).day())}
            />
          ))}
        </Flex>
        <Flex>
          <Flex
            width={1}
            css={{
              position: "relative",
              maxWidth: "374px",
              margin: "20px auto",
            }}
            flexDirection="row"
          >
            <CardRow alignItems="flex-start" textAlign="right">
              {e1.map((event, i) => (
                <Box
                  key={`${event.id}${event.title}`}
                  css={{
                    marginTop: i % 2 === 0 ? "3px" : 0,
                    maxWidth: "calc(50% - 10px)",
                    width: "100%",
                    position: "absolute",
                    top: `${calculateTopOffset(event.start, startTime)}px`,
                  }}
                >
                  <EventCard
                    {...event}
                    rescheduled={event.timeHasChanged}
                    small={isLessThan45Min(event)}
                    height={calculateEventCardHeight(event.start, event.end)}
                    title={event.act?.title || event.title}
                    start={event.startTime}
                    end={event.endTime}
                    image={event.act?.image.fluid}
                    venue={event.eventVenue.title}
                  />
                </Box>
              ))}
            </CardRow>
            <Flex css={{ position: "relative" }} flexDirection="column">
              <Timeline
                formatter={(date) => d(date).format("HH:mm")}
                fromTime={new Date(startTime)}
                toTime={new Date(endTime)}
                interval={INTERVAL_MINUTES}
              >
                {(time, i, stack) => (
                  <Box
                    key={`${time}.${i}`}
                    css={{
                      height: `${INTERVAL_PX}px`,
                      width: "11px",
                      position: "relative",
                    }}
                  >
                    <Paragraph
                      css={{
                        color: i % 2 === 0 ? "#b1fc00" : "#f401fc",
                        fontWeight: "700",
                        transform: "rotate(90deg)",
                      }}
                      fontSize="10px"
                    >
                      {time.toString()}
                    </Paragraph>
                    {i !== stack.length - 1 ? (
                      <Box
                        key={time}
                        bg="red"
                        css={{
                          backgroundImage: `linear-gradient(to ${
                            i % 2 === 0 ? "top" : "bottom"
                          }, #f401fc, #ba63ff, #7a84ff, #3798ff, #00a4ff, #00b3ff, #00c2ff, #00cfff, #00e3f5, #00f3c9, #24fc80, #b1fc00)`,
                          left: "4px",
                          top: "36px",
                          bottom: "6px",
                          width: "2px",
                          borderRadius: "4px",
                          position: "absolute",
                        }}
                      />
                    ) : null}
                  </Box>
                )}
              </Timeline>
            </Flex>
            <CardRow alignItems="flex-end">
              {e2.map((event, i) => (
                <Box
                  key={`${event.id}${event.title}`}
                  css={{
                    marginTop: i % 2 === 0 ? "3px" : 0,
                    maxWidth: "calc(50% - 10px)",
                    width: "100%",
                    position: "absolute",
                    top: `${calculateTopOffset(event.start, startTime)}px`,
                  }}
                >
                  <EventCard
                    {...event}
                    rescheduled={event.timeHasChanged}
                    small={isLessThan45Min(event)}
                    height={calculateEventCardHeight(event.start, event.end)}
                    title={event.act?.title || event.title}
                    start={event.startTime}
                    end={event.endTime}
                    image={event.act?.image.fluid}
                    venue={event.eventVenue.title}
                  />
                </Box>
              ))}
            </CardRow>
          </Flex>
        </Flex>
      </Box>
      <ColorOverlay
        timeSchedule={{
          12: "rgb(5, 96, 62, .9)",
          17: "rgb(6, 76, 50, .9)",
          19: "rgba(0, 0, 0, 0.7)",
          20: "rgba(0, 0, 0, 0.8)",
          22: "rgba(0, 0, 0, 0.9)",
          0: "rgba(0, 0, 0, 0.9)",
          2: "rgba(0, 0, 0, 0.9)",
          3: "rgba(0, 0, 0, 0.8)",
          4: "rgba(0, 0, 0, 0.7)",
          5: "rgb(6, 76, 50, .9)",
          6: "rgb(5, 96, 62, .9)",
        }}
        colors={[
          "rgba(0, 0, 0, 0.9)",
          "rgba(0, 0, 0, 0.9)",
          "rgba(0, 0, 0, 0.9)",
        ]}
      />
    </div>
  );
};

SchemaPage.propTypes = {
  data: PropTypes.object.isRequired,
};

export const query = graphql`
  {
    flags: allContentfulFeatures {
      edges {
        node {
          hideSchema
        }
      }
    }
    events: allContentfulSchemaCollection {
      edges {
        node {
          artistEvents {
            id
            start(formatString: "YYYY-MM-DDTHH:mm+02:00")
            end(formatString: "YYYY-MM-DDTHH:mm+02:00")
            startTime: start(formatString: "HH:mm")
            endTime: end(formatString: "HH:mm")
            title
            description {
              description
            }
            cancelled
            timeHasChanged
            eventVenue {
              title
            }
            act {
              id
              slug
              image {
                fluid(
                  resizingBehavior: FILL
                  maxHeight: 160
                  maxWidth: 160
                  cropFocus: FACES
                ) {
                  base64
                  aspectRatio
                  src
                  srcSet
                  sizes
                }
              }
              name
            }
          }
        }
      }
    }
  }
`;
export default SchemaPage;
